import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import AppSidebar from '@/components/AppSidebar';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import {
  Battery, Shield, ShieldAlert, Lock, Leaf, Activity, MapPin, Menu, LogOut,
  ListTodo, BarChart3, RefreshCw, Play, AlertTriangle, Cpu, Network, Radar,
  Bug, FileCheck, Server, Wifi
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SEV_STYLE = {
  Critical: 'bg-red-100 text-red-800 border border-red-300',
  High: 'bg-orange-100 text-orange-800 border border-orange-300',
  Medium: 'bg-yellow-100 text-yellow-800 border border-yellow-300',
  Low: 'bg-blue-100 text-blue-700 border border-blue-200',
};

const TABS = [
  { key: 'dashboard', label: 'Dashboard', icon: BarChart3 },
  { key: 'ids', label: 'OT Network IDS', icon: Network },
  { key: 'integrity', label: 'Device Integrity', icon: Cpu },
  { key: 'access', label: 'Command & Access', icon: Lock },
  { key: 'anomaly', label: 'Anomaly Detection', icon: Radar },
  { key: 'intel', label: 'Threat Intel', icon: Bug },
  { key: 'incidents', label: 'Incidents', icon: AlertTriangle },
  { key: 'map', label: 'Threat Map', icon: MapPin },
  { key: 'compliance', label: 'Compliance', icon: FileCheck },
];

const NavButton = ({ icon: Icon, label, onClick, active, open }) => (
  <Button variant="ghost" onClick={onClick}
    className={`w-full justify-start ${active ? 'bg-red-50 text-red-700' : 'text-gray-700 hover:text-black hover:bg-gray-100'}`}>
    <Icon className="w-5 h-5" />
    {open && <span className="ml-3 text-left">{label}</span>}
  </Button>
);

export default function CyberPhysicalSecurityPage() {
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [tab, setTab] = useState('dashboard');
  const [loading, setLoading] = useState(true);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [user] = useState(JSON.parse(localStorage.getItem('user') || '{}'));

  const [summary, setSummary] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [devices, setDevices] = useState([]);
  const [events, setEvents] = useState([]);
  const [incidents, setIncidents] = useState([]);
  const [intel, setIntel] = useState([]);
  const [compliance, setCompliance] = useState([]);
  const [mapPoints, setMapPoints] = useState([]);
  const timerRef = useRef(null);

  const token = localStorage.getItem('token');
  const config = { headers: { Authorization: `Bearer ${token}` } };

  const loadAll = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const [s, a, d, e, inc, ti, comp, mp] = await Promise.all([
        axios.get(`${API}/cyber/summary`, config),
        axios.get(`${API}/cyber/alerts`, config),
        axios.get(`${API}/cyber/devices`, config),
        axios.get(`${API}/cyber/network-events`, config),
        axios.get(`${API}/cyber/incidents`, config),
        axios.get(`${API}/cyber/threat-intel`, config),
        axios.get(`${API}/cyber/compliance`, config),
        axios.get(`${API}/cyber/threat-map`, config),
      ]);
      setSummary(s.data); setAlerts(a.data); setDevices(d.data); setEvents(e.data);
      setIncidents(inc.data); setIntel(ti.data); setCompliance(comp.data); setMapPoints(mp.data.points || []);
    } catch (err) {
      if (err.response?.status === 401) { localStorage.clear(); navigate('/login'); }
    } finally {
      if (!silent) setLoading(false);
    }
  }, [navigate]);

  useEffect(() => { loadAll(); }, [loadAll]);

  useEffect(() => {
    if (autoRefresh) {
      timerRef.current = setInterval(() => loadAll(true), 30000);
      return () => clearInterval(timerRef.current);
    }
  }, [autoRefresh, loadAll]);

  const generateTestData = async () => {
    try {
      await axios.post(`${API}/cyber/generate`, {}, config);
      toast.success('Cyber test data regenerated');
      loadAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to generate');
    }
  };

  const updateIncident = async (id, status) => {
    try {
      await axios.patch(`${API}/cyber/incidents/${id}?status=${encodeURIComponent(status)}`, {}, config);
      toast.success(`Incident ${status}`);
      loadAll(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    }
  };

  const logout = () => { localStorage.clear(); navigate('/login'); };
  const isGuest = user?.role === 'guest';

  // Live ticker items
  const ticker = (summary?.recent_alerts || []).slice(0, 8);

  return (
    <div className="flex h-screen bg-gray-50 overflow-hidden">
      {/* Sidebar */}
      <AppSidebar />

      {/* Main */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header */}
        <header className="bg-white border-b border-gray-200 px-6 py-4">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="icon" onClick={() => setSidebarOpen(!sidebarOpen)}><Menu className="w-5 h-5" /></Button>
              <div>
                <h1 className="text-2xl font-bold text-black flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  <ShieldAlert className="w-6 h-6 text-red-600" /> Cyber-Physical Security
                </h1>
                <p className="text-sm text-gray-500">OT Network Monitoring · Passive IDS · Threat Intelligence</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-2 px-3 py-1.5 bg-red-50 border border-red-200 rounded-lg">
                <span className="w-2 h-2 rounded-full bg-red-600 animate-pulse" />
                <span className="text-xs font-semibold text-red-700">LIVE THREAT FEED</span>
                <span className="text-xs text-gray-600">{ticker.length} active</span>
              </div>
              <Button variant="outline" size="sm" onClick={() => loadAll()} data-testid="cyber-refresh"><RefreshCw className="w-4 h-4 mr-1" />Refresh</Button>
              {!isGuest && (
                <Button size="sm" className="bg-gradient-to-r from-red-500 to-rose-600 text-white" onClick={generateTestData} data-testid="cyber-generate">
                  <Play className="w-4 h-4 mr-1" />Generate Test Data
                </Button>
              )}
              <button onClick={() => setAutoRefresh(!autoRefresh)} className={`text-xs px-2 py-1 rounded ${autoRefresh ? 'text-green-700 bg-green-50' : 'text-gray-500 bg-gray-100'}`}>
                Auto-refresh 30s {autoRefresh ? 'ON' : 'OFF'}
              </button>
            </div>
          </div>
          {/* Ticker */}
          {ticker.length > 0 && (
            <div className="mt-3 overflow-hidden bg-gray-900 rounded-md">
              <div className="flex gap-8 px-4 py-1.5 whitespace-nowrap animate-[marquee_30s_linear_infinite] hover:[animation-play-state:paused]" style={{ animation: 'marquee 28s linear infinite' }}>
                {ticker.concat(ticker).map((t, i) => (
                  <span key={i} className="text-xs text-gray-200 inline-flex items-center gap-2">
                    <span className={`px-1.5 py-0.5 rounded ${SEV_STYLE[t.severity]}`}>{t.severity?.toUpperCase()}</span>
                    {t.title} · {t.category} · {new Date(t.timestamp).toLocaleString()}
                  </span>
                ))}
              </div>
            </div>
          )}
        </header>

        {/* Tabs */}
        <div className="bg-white border-b border-gray-200 px-4 flex gap-1 overflow-x-auto">
          {TABS.map((t) => (
            <button key={t.key} onClick={() => setTab(t.key)} data-testid={`cyber-tab-${t.key}`}
              className={`px-3 py-3 text-sm font-medium whitespace-nowrap border-b-2 transition-colors flex items-center gap-1.5
                ${tab === t.key ? 'border-red-600 text-red-700' : 'border-transparent text-gray-500 hover:text-black'}`}>
              <t.icon className="w-4 h-4" />{t.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <main className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex items-center justify-center h-64 text-gray-400">Loading cyber telemetry…</div>
          ) : (
            <>
              {tab === 'dashboard' && summary && <DashboardTab summary={summary} />}
              {tab === 'ids' && <IDSTab events={events} />}
              {tab === 'integrity' && <IntegrityTab devices={devices} />}
              {tab === 'access' && <AlertsTab alerts={alerts} category="Command & Access" extra="Remote Access" title="Command & Access Events" />}
              {tab === 'anomaly' && <AlertsTab alerts={alerts} category="Behavioral Anomaly" title="Behavioral Anomaly Detection" />}
              {tab === 'intel' && <IntelTab intel={intel} />}
              {tab === 'incidents' && <IncidentsTab incidents={incidents} updateIncident={updateIncident} isGuest={isGuest} />}
              {tab === 'map' && <ThreatMapTab points={mapPoints} />}
              {tab === 'compliance' && <ComplianceTab compliance={compliance} />}
            </>
          )}
        </main>
      </div>
      <style>{`@keyframes marquee { 0% { transform: translateX(0); } 100% { transform: translateX(-50%); } }`}</style>
    </div>
  );
}

/* ---------- KPI helper ---------- */
const Kpi = ({ label, value, sub, color = 'text-black', icon: Icon, iconColor = 'text-red-600' }) => (
  <Card className="bg-white border-gray-200">
    <CardHeader className="pb-2">
      <CardTitle className="text-xs font-medium text-gray-500 flex items-center justify-between uppercase tracking-wide">
        {label}{Icon && <Icon className={`w-4 h-4 ${iconColor}`} />}
      </CardTitle>
    </CardHeader>
    <CardContent>
      <div className={`text-3xl font-bold ${color}`} style={{ fontFamily: 'Manrope, sans-serif' }}>{value}</div>
      {sub && <p className="text-xs text-gray-500 mt-1">{sub}</p>}
    </CardContent>
  </Card>
);

/* ---------- Dashboard tab ---------- */
function DashboardTab({ summary }) {
  const ot = summary.ot_traffic;
  const maxCat = Math.max(...Object.values(summary.category_counts), 1);
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <Kpi label="Total Alerts" value={summary.total_alerts} icon={Shield} />
        <Kpi label="Critical" value={summary.critical} color="text-red-600" icon={AlertTriangle} />
        <Kpi label="High" value={summary.high} color="text-orange-600" icon={AlertTriangle} iconColor="text-orange-500" />
        <Kpi label="Active Incidents" value={summary.active_incidents} icon={Activity} />
        <Kpi label="Vulnerable Firmware" value={summary.vulnerable_firmware} color="text-red-600" icon={Bug} />
        <Kpi label="New Devices" value={summary.new_devices} icon={Server} iconColor="text-blue-500" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* OT Network Traffic */}
        <Card className="bg-white border-gray-200">
          <CardHeader><CardTitle className="text-black text-lg flex items-center gap-2"><Wifi className="w-5 h-5 text-red-600" />OT Network Traffic</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <div className="text-4xl font-bold text-black">{ot.total}<span className="text-sm font-normal text-gray-500 ml-2">Total Events</span></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-green-50 border border-green-200 rounded-lg p-3"><div className="text-2xl font-bold text-green-700">{ot.read}</div><div className="text-xs text-green-700">Read Ops</div></div>
              <div className="bg-red-50 border border-red-200 rounded-lg p-3"><div className="text-2xl font-bold text-red-700">{ot.write}</div><div className="text-xs text-red-700">Write Ops</div></div>
            </div>
            <div className="bg-rose-50 border border-rose-200 rounded-lg p-3 flex items-center justify-between">
              <span className="text-sm text-rose-700 font-medium">Anomalous</span>
              <span className="text-lg font-bold text-rose-700">{ot.anomalous} events</span>
            </div>
          </CardContent>
        </Card>

        {/* Alert categories */}
        <Card className="bg-white border-gray-200">
          <CardHeader><CardTitle className="text-black text-lg">Alert Categories</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            {Object.entries(summary.category_counts).map(([k, v]) => (
              <div key={k}>
                <div className="flex justify-between text-sm mb-1"><span className="text-gray-700">{k}</span><span className="font-semibold text-black">{v}</span></div>
                <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden"><div className="h-full bg-red-500" style={{ width: `${(v / maxCat) * 100}%` }} /></div>
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Site threat scores */}
        <Card className="bg-white border-gray-200">
          <CardHeader><CardTitle className="text-black text-lg">Site Threat Scores</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {summary.site_threat_scores.map((s) => (
              <div key={s.site}>
                <div className="flex justify-between text-sm mb-1"><span className="text-gray-700 truncate">{s.site}</span><span className="font-semibold text-black">{s.score}</span></div>
                <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
                  <div className={`h-full ${s.score >= 80 ? 'bg-red-600' : s.score >= 50 ? 'bg-orange-500' : 'bg-yellow-400'}`} style={{ width: `${s.score}%` }} />
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent alerts */}
        <Card className="bg-white border-gray-200">
          <CardHeader><CardTitle className="text-black text-lg">Recent Alerts</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {summary.recent_alerts.map((a) => (
              <div key={a.id} className="flex items-center justify-between p-2 border border-gray-100 rounded-lg">
                <div className="min-w-0">
                  <div className="text-sm font-medium text-black truncate">{a.title}</div>
                  <div className="text-xs text-gray-500">{a.category} · {new Date(a.timestamp).toLocaleString()}</div>
                </div>
                <span className={`text-xs px-2 py-0.5 rounded ${SEV_STYLE[a.severity]}`}>{a.severity}</span>
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Site exposure scores */}
        <Card className="bg-white border-gray-200">
          <CardHeader><CardTitle className="text-black text-lg">Site Exposure Scores (Top 10)</CardTitle></CardHeader>
          <CardContent className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-gray-500 border-b border-gray-200">
                <th className="py-2">Site</th><th>Exposure</th><th>Devices</th><th>Vulnerable</th><th>Max CVSS</th></tr></thead>
              <tbody>
                {summary.site_exposure_scores.map((s) => (
                  <tr key={s.site} className="border-b border-gray-100">
                    <td className="py-2 text-gray-800 truncate max-w-[140px]">{s.site}</td>
                    <td className="font-semibold text-red-600">{s.exposure}</td>
                    <td>{s.devices}</td><td className="text-orange-600">{s.vulnerable}</td>
                    <td className={s.max_cvss >= 7 ? 'text-red-600 font-semibold' : 'text-gray-700'}>{s.max_cvss}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

/* ---------- OT Network IDS tab ---------- */
function IDSTab({ events }) {
  return (
    <Card className="bg-white border-gray-200">
      <CardHeader><CardTitle className="text-black text-lg">OT Network IDS — Passive Packet Inspection ({events.length})</CardTitle></CardHeader>
      <CardContent className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="text-left text-gray-500 border-b border-gray-200">
            <th className="py-2">Time</th><th>Site</th><th>Protocol</th><th>Func</th><th>Op</th><th>Source</th><th>Dest</th><th>Status</th></tr></thead>
          <tbody>
            {events.map((e) => (
              <tr key={e.id} className={`border-b border-gray-100 ${e.anomalous ? 'bg-red-50' : ''}`}>
                <td className="py-2 text-gray-500">{new Date(e.timestamp).toLocaleTimeString()}</td>
                <td className="text-gray-800 truncate max-w-[120px]">{e.asset_name}</td>
                <td>{e.protocol}</td><td>{e.function_code}</td>
                <td><span className={e.operation === 'Write' ? 'text-red-600 font-medium' : 'text-green-600'}>{e.operation}</span></td>
                <td className="font-mono text-xs">{e.src}</td><td className="font-mono text-xs">{e.dst}</td>
                <td>{e.anomalous ? <span className="text-xs px-2 py-0.5 rounded bg-red-100 text-red-700">Anomalous</span> : <span className="text-xs text-gray-400">Normal</span>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </CardContent>
    </Card>
  );
}

/* ---------- Device Integrity tab ---------- */
function IntegrityTab({ devices }) {
  return (
    <Card className="bg-white border-gray-200">
      <CardHeader><CardTitle className="text-black text-lg">Device Integrity & Firmware ({devices.length})</CardTitle></CardHeader>
      <CardContent className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="text-left text-gray-500 border-b border-gray-200">
            <th className="py-2">Device</th><th>Site</th><th>Chemistry</th><th>IP</th><th>Firmware</th><th>Integrity</th><th>CVSS</th><th>Vulnerable</th></tr></thead>
          <tbody>
            {devices.map((d) => (
              <tr key={d.id} className="border-b border-gray-100">
                <td className="py-2 text-gray-800">{d.device_type}{d.is_new && <span className="ml-1 text-[10px] px-1 rounded bg-blue-100 text-blue-700">NEW</span>}</td>
                <td className="truncate max-w-[120px]">{d.asset_name}</td>
                <td>{d.chemistry}</td><td className="font-mono text-xs">{d.ip}</td>
                <td>{d.firmware_version}{d.firmware_outdated && <span className="ml-1 text-[10px] px-1 rounded bg-red-100 text-red-700">OUTDATED</span>}</td>
                <td><span className={d.integrity === 'OK' ? 'text-green-600' : d.integrity === 'Drift' ? 'text-yellow-600' : 'text-red-600 font-semibold'}>{d.integrity}</span></td>
                <td className={d.cvss >= 7 ? 'text-red-600 font-semibold' : d.cvss >= 4 ? 'text-orange-600' : 'text-gray-600'}>{d.cvss}</td>
                <td>{d.vulnerable ? <span className="text-xs px-2 py-0.5 rounded bg-red-100 text-red-700">Yes</span> : <span className="text-xs text-green-600">No</span>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </CardContent>
    </Card>
  );
}

/* ---------- Generic alerts tab (Command&Access / Anomaly) ---------- */
function AlertsTab({ alerts, category, extra, title }) {
  const filtered = alerts.filter((a) => a.category === category || (extra && a.category === extra));
  return (
    <Card className="bg-white border-gray-200">
      <CardHeader><CardTitle className="text-black text-lg">{title} ({filtered.length})</CardTitle></CardHeader>
      <CardContent className="space-y-2">
        {filtered.length === 0 && <p className="text-gray-400 text-sm">No events in this category.</p>}
        {filtered.map((a) => (
          <div key={a.id} className="flex items-center justify-between p-3 border border-gray-100 rounded-lg">
            <div className="min-w-0">
              <div className="text-sm font-medium text-black">{a.title}</div>
              <div className="text-xs text-gray-500">{a.category} · {a.device_type} · {new Date(a.timestamp).toLocaleString()}</div>
              <div className="text-xs text-gray-600 mt-1">{a.detail}</div>
            </div>
            <div className="flex flex-col items-end gap-1">
              <span className={`text-xs px-2 py-0.5 rounded ${SEV_STYLE[a.severity]}`}>{a.severity}</span>
              <span className="text-[10px] text-gray-400">{a.status}</span>
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

/* ---------- Threat Intel tab ---------- */
function IntelTab({ intel }) {
  return (
    <Card className="bg-white border-gray-200">
      <CardHeader><CardTitle className="text-black text-lg">Threat Intelligence Indicators ({intel.length})</CardTitle></CardHeader>
      <CardContent className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="text-left text-gray-500 border-b border-gray-200">
            <th className="py-2">Indicator</th><th>Type</th><th>Actor</th><th>Severity</th><th>Confidence</th><th>First Seen</th></tr></thead>
          <tbody>
            {intel.map((i) => (
              <tr key={i.id} className="border-b border-gray-100">
                <td className="py-2 font-mono text-xs text-gray-800 truncate max-w-[200px]">{i.indicator}</td>
                <td><Badge variant="outline" className="text-xs">{i.type}</Badge></td>
                <td className="text-gray-700">{i.actor}</td>
                <td><span className={`text-xs px-2 py-0.5 rounded ${SEV_STYLE[i.severity]}`}>{i.severity}</span></td>
                <td><div className="flex items-center gap-2"><div className="w-16 h-1.5 bg-gray-100 rounded"><div className="h-full bg-red-500 rounded" style={{ width: `${i.confidence}%` }} /></div>{i.confidence}%</div></td>
                <td className="text-gray-500">{new Date(i.first_seen).toLocaleDateString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </CardContent>
    </Card>
  );
}

/* ---------- Incidents tab ---------- */
function IncidentsTab({ incidents, updateIncident, isGuest }) {
  const next = { Open: 'Investigating', Investigating: 'Contained', Contained: 'Closed' };
  return (
    <div className="space-y-3">
      {incidents.length === 0 && <p className="text-gray-400 text-sm">No incidents.</p>}
      {incidents.map((i) => (
        <Card key={i.id} className={`bg-white border-l-4 ${i.severity === 'Critical' ? 'border-l-red-600' : 'border-l-orange-500'} border-gray-200`}>
          <CardContent className="p-4 flex items-center justify-between gap-4 flex-wrap">
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <span className={`text-xs px-2 py-0.5 rounded ${SEV_STYLE[i.severity]}`}>{i.severity}</span>
                <span className="text-sm font-semibold text-black">{i.title}</span>
              </div>
              <div className="text-xs text-gray-500 mt-1">{i.asset_name} · {i.mitre} · Owner: {i.owner} · Opened {new Date(i.opened_at).toLocaleString()}</div>
            </div>
            <div className="flex items-center gap-2">
              <span className={`text-xs px-2 py-1 rounded font-medium ${i.status === 'Closed' ? 'bg-green-100 text-green-700' : i.status === 'Contained' ? 'bg-blue-100 text-blue-700' : 'bg-yellow-100 text-yellow-700'}`}>{i.status}</span>
              {!isGuest && next[i.status] && (
                <Button size="sm" variant="outline" onClick={() => updateIncident(i.id, next[i.status])}>Mark {next[i.status]}</Button>
              )}
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

/* ---------- Threat Map tab ---------- */
function ThreatMapTab({ points }) {
  const lats = points.map((p) => p.latitude), lons = points.map((p) => p.longitude);
  const minLat = Math.min(...lats), maxLat = Math.max(...lats), minLon = Math.min(...lons), maxLon = Math.max(...lons);
  const x = (lon) => ((lon - minLon) / (maxLon - minLon || 1)) * 100;
  const y = (lat) => (1 - (lat - minLat) / (maxLat - minLat || 1)) * 100;
  const color = (s) => s >= 80 ? '#dc2626' : s >= 50 ? '#f97316' : s >= 20 ? '#eab308' : '#22c55e';
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <Card className="bg-white border-gray-200 lg:col-span-2">
        <CardHeader><CardTitle className="text-black text-lg flex items-center gap-2"><MapPin className="w-5 h-5 text-red-600" />Geographic Threat Distribution</CardTitle></CardHeader>
        <CardContent>
          <div className="relative w-full bg-gradient-to-br from-gray-900 to-gray-800 rounded-lg" style={{ height: 420 }}>
            {points.map((p) => (
              <div key={p.asset_id} className="absolute -translate-x-1/2 -translate-y-1/2 group" style={{ left: `${x(p.longitude)}%`, top: `${y(p.latitude)}%` }}>
                <span className="block rounded-full" style={{ width: 14, height: 14, backgroundColor: color(p.threat_score), boxShadow: `0 0 12px ${color(p.threat_score)}` }} />
                <div className="hidden group-hover:block absolute left-4 top-0 bg-white text-xs rounded px-2 py-1 shadow-lg whitespace-nowrap z-10">
                  <b>{p.site}</b> ({p.chemistry}) · Threat {p.threat_score}
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
      <Card className="bg-white border-gray-200">
        <CardHeader><CardTitle className="text-black text-lg">Sites by Threat</CardTitle></CardHeader>
        <CardContent className="space-y-2 max-h-[420px] overflow-y-auto">
          {[...points].sort((a, b) => b.threat_score - a.threat_score).map((p) => (
            <div key={p.asset_id} className="flex items-center justify-between text-sm border-b border-gray-100 pb-1">
              <span className="flex items-center gap-2"><span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: color(p.threat_score) }} />{p.site}</span>
              <span className="font-semibold text-black">{p.threat_score}</span>
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
}

/* ---------- Compliance tab ---------- */
function ComplianceTab({ compliance }) {
  const avg = compliance.length ? Math.round(compliance.reduce((s, c) => s + c.score, 0) / compliance.length) : 0;
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Kpi label="Overall Posture" value={`${avg}%`} color={avg >= 80 ? 'text-green-600' : avg >= 60 ? 'text-yellow-600' : 'text-red-600'} icon={FileCheck} />
        <Kpi label="Pass" value={compliance.filter((c) => c.status === 'Pass').length} color="text-green-600" />
        <Kpi label="Partial" value={compliance.filter((c) => c.status === 'Partial').length} color="text-yellow-600" />
        <Kpi label="Fail" value={compliance.filter((c) => c.status === 'Fail').length} color="text-red-600" />
      </div>
      <Card className="bg-white border-gray-200">
        <CardHeader><CardTitle className="text-black text-lg">Compliance Controls (IEC 62443 · NIST CSF · NERC CIP · ISO 27001)</CardTitle></CardHeader>
        <CardContent className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-gray-500 border-b border-gray-200"><th className="py-2">Framework</th><th>Control</th><th>Score</th><th>Status</th><th>Last Assessed</th></tr></thead>
            <tbody>
              {compliance.map((c) => (
                <tr key={c.id} className="border-b border-gray-100">
                  <td className="py-2"><Badge variant="outline" className="text-xs">{c.framework}</Badge></td>
                  <td className="text-gray-800">{c.control}</td>
                  <td className="font-semibold">{c.score}%</td>
                  <td><span className={`text-xs px-2 py-0.5 rounded ${c.status === 'Pass' ? 'bg-green-100 text-green-700' : c.status === 'Partial' ? 'bg-yellow-100 text-yellow-700' : 'bg-red-100 text-red-700'}`}>{c.status}</span></td>
                  <td className="text-gray-500">{new Date(c.last_assessed).toLocaleDateString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}
